import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import {
  Button,
  ButtonVariant,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import api from "../../../../util/api";
import { getApiErrorMessageWithForbiddenFallback } from "../../../../util/apiErrorMessage";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import { walletFeeAdminProviderLabel } from "../../billing/components/walletFeeFormConstants";

type QaStatus = Awaited<ReturnType<typeof api.getCompanyWalletBillingQa>>;
type QaLedger = QaStatus["ledgers"][number];
type QaBankAccount = QaStatus["bankAccounts"][number];

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
  font-family: Montserrat, sans-serif;
`;

const WarningBanner = styled.div`
  background: rgba(249, 160, 0, 0.17);
  border: 1px solid #f9a000;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 13px;
  color: #13273f;
  line-height: 1.5;
`;

const InfoBanner = styled(WarningBanner)`
  background: rgba(2, 122, 255, 0.08);
  border-color: #027aff;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  padding: 16px;
`;

const CardTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: #13273f;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
`;

const Select = styled.select`
  min-width: 220px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #dbe3eb;
  padding: 0 10px;
  font-size: 13px;
`;

const Input = styled.input`
  min-width: 200px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #dbe3eb;
  padding: 0 10px;
  font-size: 13px;
`;

const LedgerCard = styled(Card)`
  margin-top: 8px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px 16px;
  font-size: 12px;
  color: #374151;
`;

const MetaItem = styled.div`
  strong {
    display: block;
    color: #6b7280;
    font-weight: 500;
    margin-bottom: 2px;
  }
`;

const Tag = styled.span<{ $ok?: boolean }>`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) =>
    p.$ok ? "rgba(44, 209, 158, 0.2)" : "rgba(129, 129, 165, 0.2)"};
  color: ${(p) => (p.$ok ? "#00bc82" : "#5c6784")};
`;

const ResultList = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 12px;
  color: #374151;
`;

const formatPence = (p: number) =>
  `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-GB") : "—";

export const CompanyWalletBillingQaTab: React.FC<{ companyId: string }> = ({
  companyId,
}) => {
  const { showNotification } = useNotification();
  const [data, setData] = useState<QaStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [patching, setPatching] = useState<string | null>(null);
  const [connectionFilter, setConnectionFilter] = useState("");
  const [payoutDaysAgo, setPayoutDaysAgo] = useState("1");
  const [lastPatchResult, setLastPatchResult] = useState<{
    applied: Array<{ target: string; field: string; value: string | null }>;
    skipped: Array<{ target: string; reason: string }>;
  } | null>(null);
  const [advancedLedgerId, setAdvancedLedgerId] = useState("");
  const [advancedFields, setAdvancedFields] = useState({
    lastWalletFeeInterimChargeAt: "",
    lastWalletFeeEopMonthKey: "",
    lastPerAccountMonthlyChargedMonthKey: "",
    interimTransactionFeesChargedPenceThisMonth: "",
    lastCompletedPeriodicWalletPayoutAt: "",
    advancedBankAccountId: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await api.getCompanyWalletBillingQa(companyId);
      setData(payload);
      if (!connectionFilter && payload.connections[0]) {
        setConnectionFilter(payload.connections[0].walletConnectionId);
      }
      if (!advancedLedgerId && payload.ledgers[0]) {
        setAdvancedLedgerId(payload.ledgers[0].ledgerId);
      }
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessageWithForbiddenFallback(
          e,
          "Failed to load wallet billing QA status"
        ),
        NotificationTypes.DANGER
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, showNotification]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredLedgers = useMemo(() => {
    const rows = data?.ledgers ?? [];
    if (!connectionFilter) return rows;
    return rows.filter((l) => l.walletConnectionId === connectionFilter);
  }, [data?.ledgers, connectionFilter]);

  const filteredBankAccounts = useMemo(() => {
    const rows = data?.bankAccounts ?? [];
    if (!connectionFilter) return rows;
    return rows.filter((b) => b.walletConnectionId === connectionFilter);
  }, [data?.bankAccounts, connectionFilter]);

  const runPreset = async (
    key: string,
    body: Parameters<typeof api.patchCompanyWalletBillingQa>[1]
  ) => {
    if (!data?.mutationsAllowed) {
      showNotification(
        "Mark this company as a test company to apply QA presets",
        NotificationTypes.WARNING
      );
      return;
    }
    setPatching(key);
    try {
      const result = await api.patchCompanyWalletBillingQa(companyId, body);
      setLastPatchResult(result);
      showNotification("QA preset applied", NotificationTypes.SUCCESS);
      await load();
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessageWithForbiddenFallback(e, "Preset failed"),
        NotificationTypes.DANGER
      );
    } finally {
      setPatching(null);
    }
  };

  const saveAdvanced = async () => {
    if (!advancedLedgerId && !advancedFields.advancedBankAccountId) return;
    const fields: Record<string, string | number | null> = {};
    if (advancedFields.lastWalletFeeInterimChargeAt) {
      fields.lastWalletFeeInterimChargeAt = new Date(
        advancedFields.lastWalletFeeInterimChargeAt
      ).toISOString();
    }
    if (advancedFields.lastWalletFeeEopMonthKey) {
      fields.lastWalletFeeEopMonthKey = advancedFields.lastWalletFeeEopMonthKey;
    }
    if (advancedFields.lastPerAccountMonthlyChargedMonthKey) {
      fields.lastPerAccountMonthlyChargedMonthKey =
        advancedFields.lastPerAccountMonthlyChargedMonthKey;
    }
    if (advancedFields.interimTransactionFeesChargedPenceThisMonth) {
      fields.interimTransactionFeesChargedPenceThisMonth = Number(
        advancedFields.interimTransactionFeesChargedPenceThisMonth
      );
    }
    if (advancedFields.lastCompletedPeriodicWalletPayoutAt) {
      fields.lastCompletedPeriodicWalletPayoutAt = new Date(
        advancedFields.lastCompletedPeriodicWalletPayoutAt
      ).toISOString();
    }

    await runPreset("advanced", {
      mode: "patch",
      ledgerId: advancedLedgerId || undefined,
      bankAccountId: advancedFields.advancedBankAccountId || undefined,
      fields,
    });
  };

  const renderLedger = (ledger: QaLedger) => (
    <LedgerCard key={ledger.ledgerId}>
      <CardTitle>
        Ledger {ledger.ledgerId.slice(-6)} ·{" "}
        {walletFeeAdminProviderLabel(ledger.walletProvider)}
      </CardTitle>
      <MetaGrid>
        <MetaItem>
          <strong>Interim due</strong>
          <Tag $ok={ledger.computed.interimDueYet}>
            {ledger.computed.interimDueYet ? "Yes" : "No"}
          </Tag>
        </MetaItem>
        <MetaItem>
          <strong>Interim interval</strong>
          {ledger.computed.interimInterval ?? "EOP only"}
        </MetaItem>
        <MetaItem>
          <strong>Next EOP month</strong>
          {ledger.computed.nextEopMonthKey ?? "—"}
        </MetaItem>
        <MetaItem>
          <strong>SOP charged this month</strong>
          <Tag $ok={!ledger.computed.sopAlreadyChargedThisMonth}>
            {ledger.computed.sopAlreadyChargedThisMonth ? "Yes" : "No"}
          </Tag>
        </MetaItem>
        <MetaItem>
          <strong>Inbound volume</strong>
          {formatPence(ledger.counters.billableInboundVolumePence ?? 0)}
        </MetaItem>
        <MetaItem>
          <strong>Inbound tx count</strong>
          {ledger.counters.billableInboundTransactionCount ?? 0}
        </MetaItem>
        <MetaItem>
          <strong>Last interim charge</strong>
          {formatDate(ledger.timing.lastWalletFeeInterimChargeAt)}
        </MetaItem>
        <MetaItem>
          <strong>Last EOP month</strong>
          {ledger.timing.lastWalletFeeEopMonthKey ?? "—"}
        </MetaItem>
      </MetaGrid>
      <Row style={{ marginTop: 12 }}>
        <Button
          type="button"
          variant={ButtonVariant.OUTLINE}
          disabled={!data?.mutationsAllowed || patching !== null}
          onClick={() =>
            void runPreset(`interim-${ledger.ledgerId}`, {
              mode: "preset",
              preset: "make_interim_due",
              ledgerId: ledger.ledgerId,
            })
          }
        >
          {patching === `interim-${ledger.ledgerId}`
            ? "Applying…"
            : "Make interim due"}
        </Button>
        <Button
          type="button"
          variant={ButtonVariant.OUTLINE}
          disabled={!data?.mutationsAllowed || patching !== null}
          onClick={() =>
            void runPreset(`sop-${ledger.ledgerId}`, {
              mode: "preset",
              preset: "reset_sop",
              ledgerId: ledger.ledgerId,
            })
          }
        >
          {patching === `sop-${ledger.ledgerId}` ? "Applying…" : "Reset SOP"}
        </Button>
      </Row>
    </LedgerCard>
  );

  const renderBankAccount = (bank: QaBankAccount) => (
    <LedgerCard key={bank.bankAccountId}>
      <CardTitle>Bank account {bank.bankAccountId.slice(-6)}</CardTitle>
      <MetaGrid>
        <MetaItem>
          <strong>Payout window start</strong>
          {formatDate(bank.payoutWindowStart)}
        </MetaItem>
        <MetaItem>
          <strong>Eligible txns in window</strong>
          {bank.eligibleTransactionCount}
        </MetaItem>
        <MetaItem>
          <strong>Last completed payout</strong>
          {formatDate(bank.lastCompletedPeriodicWalletPayoutAt)}
        </MetaItem>
        <MetaItem>
          <strong>Today&apos;s blocking payout</strong>
          {bank.blockingPeriodicPayoutToday ? (
            <Tag $ok={false}>
              {bank.blockingPeriodicPayoutToday.status} ·{" "}
              {bank.blockingPeriodicPayoutToday.aggregationCalendarDate} ·{" "}
              {bank.blockingPeriodicPayoutToday.payoutId.slice(-6)}
            </Tag>
          ) : (
            <Tag $ok>None</Tag>
          )}
        </MetaItem>
      </MetaGrid>
      <Row style={{ marginTop: 12 }}>
        <Field>
          Days ago
          <Input
            type="number"
            min={1}
            value={payoutDaysAgo}
            onChange={(e) => setPayoutDaysAgo(e.target.value)}
          />
        </Field>
        <Button
          type="button"
          variant={ButtonVariant.OUTLINE}
          disabled={!data?.mutationsAllowed || patching !== null}
          onClick={() =>
            void runPreset(`payout-${bank.bankAccountId}`, {
              mode: "preset",
              preset: "advance_daily_payout_window",
              bankAccountId: bank.bankAccountId,
              params: { daysAgo: Number(payoutDaysAgo) || 1 },
            })
          }
        >
          {patching === `payout-${bank.bankAccountId}`
            ? "Applying…"
            : "Advance payout window"}
        </Button>
        <Button
          type="button"
          variant={ButtonVariant.OUTLINE}
          disabled={
            !data?.mutationsAllowed ||
            patching !== null ||
            !bank.blockingPeriodicPayoutToday
          }
          onClick={() =>
            void runPreset(`cancel-payout-${bank.bankAccountId}`, {
              mode: "preset",
              preset: "cancel_todays_periodic_payout",
              bankAccountId: bank.bankAccountId,
            })
          }
        >
          {patching === `cancel-payout-${bank.bankAccountId}`
            ? "Cancelling…"
            : "Cancel today's periodic payout"}
        </Button>
      </Row>
    </LedgerCard>
  );

  if (loading && !data) {
    return (
      <Wrap>
        <Typography variant={TypographyVariant.BODY1}>Loading…</Typography>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <WarningBanner>
        <strong>QA only (non-production).</strong> Presets backdate timing
        fields so wallet crons can be tested after a manual GCP force-run. Crons
        process <strong>all merchants</strong> on the environment — use isolated
        test companies. Wallet crons do not skip <code>isTestCompany</code>{" "}
        (unlike subscription billing). Live sandbox payouts may be created when
        crons run.
      </WarningBanner>

      {!data?.isTestCompany && (
        <InfoBanner>
          This company is not marked as a test company. Status is read-only;
          presets are disabled until you mark it as a test company from the
          companies list.
        </InfoBanner>
      )}

      <Row>
        <Field>
          Wallet connection
          <Select
            value={connectionFilter}
            onChange={(e) => setConnectionFilter(e.target.value)}
          >
            <option value="">All connections</option>
            {(data?.connections ?? []).map((c) => (
              <option key={c.walletConnectionId} value={c.walletConnectionId}>
                {walletFeeAdminProviderLabel(c.provider)} (
                {c.walletConnectionId.slice(-6)})
              </option>
            ))}
          </Select>
        </Field>
        <Button
          type="button"
          variant={ButtonVariant.CONTAINED}
          disabled={loading}
          onClick={() => void load()}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
        <Link
          to={`/private/subscription/wallet-upcoming-charges`}
          style={{ fontSize: 13, alignSelf: "center" }}
        >
          Wallet fee charges
        </Link>
      </Row>

      <Card>
        <CardTitle>Interim · EOP · SOP → fee billing cron</CardTitle>
        <Typography variant={TypographyVariant.BODY2}>
          After presets, force-run{" "}
          <code>{data?.gcpCronRunbook.feeBilling.service}</code> (
          {data?.gcpCronRunbook.feeBilling.schedulerJob}) in GCP Cloud
          Scheduler.
        </Typography>
        <Row style={{ marginTop: 12 }}>
          <Button
            type="button"
            variant={ButtonVariant.OUTLINE}
            disabled={
              !data?.mutationsAllowed || patching !== null || !connectionFilter
            }
            onClick={() =>
              void runPreset("prepare-eop", {
                mode: "preset",
                preset: "prepare_eop",
                walletConnectionId: connectionFilter || undefined,
              })
            }
          >
            {patching === "prepare-eop"
              ? "Applying…"
              : "Prepare EOP (connection)"}
          </Button>
        </Row>
        <Typography variant={TypographyVariant.BODY2} style={{ marginTop: 8 }}>
          Prepares the latest <strong>closed</strong> month for EOP (rewinds{" "}
          <code>lastWalletFeeEopMonthKey</code>). If the wallet was connected
          this month, connection and ledger <code>createdAt</code> are backdated
          to that closed month so the fee billing cron can run. Then force-run
          the fee billing cron.
        </Typography>
      </Card>

      {filteredLedgers.map(renderLedger)}

      <Card>
        <CardTitle>Daily payout → daily payout cron</CardTitle>
        <Typography variant={TypographyVariant.BODY2}>
          Requires transactions with <code>walletPayoutEligibleAt</code> in the
          window. Run{" "}
          <code>{data?.gcpCronRunbook.obReconciliation.service}</code> first if
          using real wallet activity. Same-day retest:{" "}
          <strong>Cancel today&apos;s periodic payout</strong> (frees the unique
          index), then <strong>Advance payout window</strong>, then force-run{" "}
          <code>{data?.gcpCronRunbook.dailyPayout.service}</code> with new
          eligible txs (claimed txs are not reused).
        </Typography>
      </Card>

      {filteredBankAccounts.map(renderBankAccount)}

      <Card>
        <CardTitle>Advanced manual fields</CardTitle>
        <Row>
          <Field>
            Ledger
            <Select
              value={advancedLedgerId}
              onChange={(e) => setAdvancedLedgerId(e.target.value)}
            >
              {(data?.ledgers ?? []).map((l) => (
                <option key={l.ledgerId} value={l.ledgerId}>
                  {l.ledgerId.slice(-8)}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            Last interim charge (local)
            <Input
              type="datetime-local"
              value={advancedFields.lastWalletFeeInterimChargeAt}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  lastWalletFeeInterimChargeAt: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            Last EOP month (YYYY-MM)
            <Input
              placeholder="2026-03"
              value={advancedFields.lastWalletFeeEopMonthKey}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  lastWalletFeeEopMonthKey: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            SOP charged month
            <Input
              placeholder="2026-05"
              value={advancedFields.lastPerAccountMonthlyChargedMonthKey}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  lastPerAccountMonthlyChargedMonthKey: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            Interim accrued this month (pence)
            <Input
              type="number"
              value={advancedFields.interimTransactionFeesChargedPenceThisMonth}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  interimTransactionFeesChargedPenceThisMonth: e.target.value,
                }))
              }
            />
          </Field>
          <Field>
            Bank account (payout anchor)
            <Select
              value={advancedFields.advancedBankAccountId}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  advancedBankAccountId: e.target.value,
                }))
              }
            >
              <option value="">—</option>
              {(data?.bankAccounts ?? []).map((b) => (
                <option key={b.bankAccountId} value={b.bankAccountId}>
                  {b.bankAccountId.slice(-8)}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            Last completed periodic payout (local)
            <Input
              type="datetime-local"
              value={advancedFields.lastCompletedPeriodicWalletPayoutAt}
              onChange={(e) =>
                setAdvancedFields((f) => ({
                  ...f,
                  lastCompletedPeriodicWalletPayoutAt: e.target.value,
                }))
              }
            />
          </Field>
        </Row>
        <Row style={{ marginTop: 12 }}>
          <Button
            type="button"
            variant={ButtonVariant.CONTAINED}
            disabled={!data?.mutationsAllowed || patching !== null}
            onClick={() => void saveAdvanced()}
          >
            Save advanced fields
          </Button>
        </Row>
      </Card>

      {lastPatchResult && (
        <Card>
          <CardTitle>Last patch result</CardTitle>
          {lastPatchResult.applied.length > 0 && (
            <>
              <strong>Applied</strong>
              <ResultList>
                {lastPatchResult.applied.map((a, i) => (
                  <li key={i}>
                    {a.target}: {a.field} = {a.value ?? "(cleared)"}
                  </li>
                ))}
              </ResultList>
            </>
          )}
          {lastPatchResult.skipped.length > 0 && (
            <>
              <strong>Skipped</strong>
              <ResultList>
                {lastPatchResult.skipped.map((s, i) => (
                  <li key={i}>
                    {s.target}: {s.reason}
                  </li>
                ))}
              </ResultList>
            </>
          )}
        </Card>
      )}
    </Wrap>
  );
};

export default CompanyWalletBillingQaTab;
